import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

const STORAGE_KEY_SESSION = 'chargee_sessionToken'
const STORAGE_KEY_RENEW = 'chargee_renewToken'

const RETRY_MAX = 2
const RETRY_BASE_DELAY_MS = 500
const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504])

export function getStoredSessionToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEY_SESSION)
}

export function setStoredSessionToken(token: string | null): void {
  if (token) sessionStorage.setItem(STORAGE_KEY_SESSION, token)
  else sessionStorage.removeItem(STORAGE_KEY_SESSION)
}

export function getStoredRenewToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEY_RENEW)
}

export function setStoredRenewToken(token: string | null): void {
  if (token) sessionStorage.setItem(STORAGE_KEY_RENEW, token)
  else sessionStorage.removeItem(STORAGE_KEY_RENEW)
}

export function clearStoredTokens(): void {
  setStoredSessionToken(null)
  setStoredRenewToken(null)
}

// In production without VITE_API_BASE_URL we use relative /api so the app's
// proxy (server.mjs) is used and CORS is avoided. In development we go through
// the Vite proxy (/api) which targets the production Companion API.
const defaultBase = '/api'
const baseURL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? defaultBase

export const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

type RetryableConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  _retryCount?: number
  _isRefreshRequest?: boolean
}

function pickAccessToken(data: { accessToken?: string; sessionToken?: string }): string | null {
  const t = data.accessToken ?? data.sessionToken
  return typeof t === 'string' && t.length > 0 ? t : null
}

function redirectToLogin(): void {
  clearStoredTokens()
  if (window.location.pathname !== '/login' && window.location.pathname !== '/auth/complete') {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.assign(`/login?redirect=${redirect}`)
  }
}

/** In-flight refresh promise so multiple 401s don't trigger multiple refreshes. */
let refreshPromise: Promise<string | null> | null = null

async function refreshSessionToken(): Promise<string | null> {
  const renewToken = getStoredRenewToken()
  if (!renewToken) return null
  const res = await apiClient.post(
    '/v1/auth/renew-sessiontoken',
    { renewToken },
    { _isRefreshRequest: true } as Partial<RetryableConfig>
  )
  const access = pickAccessToken(res.data ?? {})
  const renew =
    typeof res.data?.renewToken === 'string' && res.data.renewToken.length > 0
      ? res.data.renewToken
      : renewToken
  if (!access) return null
  setStoredSessionToken(access)
  setStoredRenewToken(renew)
  return access
}

/**
 * Session handling for any client that talks to a backend accepting the
 * Companion session: the Bearer token, one shared renewal on 401 (always
 * through the Companion API) and retries of transient failures.
 */
export function attachAuthInterceptors(client: AxiosInstance): AxiosInstance {
  client.interceptors.request.use((config) => {
    const token = getStoredSessionToken()
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  })

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as RetryableConfig | undefined

      if (error.response?.status !== 401 || !originalRequest) {
        return Promise.reject(error)
      }

      if (originalRequest._isRefreshRequest === true || originalRequest._retry === true) {
        redirectToLogin()
        return Promise.reject(error)
      }

      if (!refreshPromise) {
        refreshPromise = refreshSessionToken()
          .catch(() => null)
          .finally(() => {
            refreshPromise = null
          })
      }

      const newToken = await refreshPromise
      if (newToken) {
        originalRequest._retry = true
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return client(originalRequest)
      }
      redirectToLogin()
      return Promise.reject(error)
    }
  )

  // Retry interceptor for transient failures
  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined
    if (!config || config._isRefreshRequest) return Promise.reject(error)
    // Cancelled requests should not be retried.
    if (axios.isCancel(error) || config.signal?.aborted) return Promise.reject(error)

    const retryCount = config._retryCount ?? 0
    const status = error.response?.status
    const isRetryable = !error.response || (status != null && RETRYABLE_STATUS_CODES.has(status))

    if (retryCount >= RETRY_MAX || !isRetryable) return Promise.reject(error)

    config._retryCount = retryCount + 1
    const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCount)
    await new Promise((r) => setTimeout(r, delay))
    return client(config)
  })

  return client
}

attachAuthInterceptors(apiClient)
